import { onClickOutside } from "@vueuse/core"
import { css } from "vite-css-in-js"
import { onMounted, renderSlot, shallowRef, type HTMLAttributes } from "vue"
import { defineComponent, inferProps, propsWithDefaults, type ReactiveComponent } from "vue-utils"

interface Props {
	zIndex?: number
	closeDefault?: () => void

	backdropAttrs?: HTMLAttributes
}

const modalBackdrop = css`
	position: fixed;
	inset: 0;

	background-color: #0006;
	backdrop-filter: blur(0.5px);

	display: flex !important;
	justify-content: center;
	align-items: center;
`

const modalStyles = css`
	position: initial;
	display: flex !important;
	flex-direction: column;
	background-color: white;

	min-width: 10rem;
	min-height: 10rem;

	max-width: calc(100vw - 0.5rem);
	max-height: calc(100vh - 0.5rem);

	.modal-content {
		flex-shrink: 1;
		flex-grow: 1;
		overflow: auto;
		padding-right: 0.5rem;
	}

	background-color: var(--modal-background-color);
	border: 1px solid var(--modal-border-color);
	border-radius: 0.5rem;
	padding: 1rem;
	box-shadow: 3px 5px 3px var(--modal-shadow-color);
`

const Modal: ReactiveComponent<Props, HTMLAttributes> = (initialProps, { slots, attrs }) => {
	const props = propsWithDefaults(initialProps, {
		zIndex: 4,
	})

	const modalRef = shallowRef<HTMLElement>()
	onClickOutside(modalRef, (click) => {
		if (click.button === 0 && props.closeDefault) {
			props.closeDefault()
		}
	})

	onMounted(() => {
		const modal = modalRef.value
		if (!modal) {
			return
		}
		const toFocus = modal.querySelectorAll("*[autofocus]")
		for (const node of toFocus) {
			if (node instanceof HTMLElement) {
				node.focus()
				break
			}
		}
	})

	return () => (
		<div class={`${modalBackdrop} modalcomponent`} style={{ zIndex: props.zIndex }} {...props.backdropAttrs}>
			<div ref={modalRef} class={modalStyles} {...attrs}>
				{renderSlot(slots, "default")}
			</div>
		</div>
	)
}

export default defineComponent(Modal, inferProps<Props>())
